<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useNotificationsStore } from '~/store/notifications';
import AppNotifications from './AppNotifications.vue';

const router = useRouter();
const notificationsStore = useNotificationsStore();

const containerRef = ref<HTMLElement | null>(null);

// Close notifications when clicking outside
const handleClickOutside = (event: MouseEvent) => {
  if (notificationsStore.isDrawerOpen && containerRef.value && !containerRef.value.contains(event.target as Node)) {
    notificationsStore.closeDrawer();
  }
};

// Close notifications when navigating to a different page
const handleRouteChange = () => {
  if (notificationsStore.isDrawerOpen) {
    notificationsStore.closeDrawer();
  }
};

onMounted(() => {
  // Add click outside listener
  document.addEventListener('click', handleClickOutside);

  // Add route change listener
  router.afterEach(handleRouteChange);
});

onBeforeUnmount(() => {
  // Remove click outside listener
  document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <div ref="containerRef" class="app-pop-up-notification-container">
    <TransitionGroup name="notification" tag="div" class="notification-stack">
      <AppNotifications />
    </TransitionGroup>
  </div>
</template>

<style lang="scss" scoped>
.app-pop-up-notification-container {
  position: fixed;
  top: 80px;
  right: 60px;
  left: auto;
  z-index: 9999;
  width: auto;
  height: auto;

  .notification-stack {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    padding: 8px;
    gap: 8px;
    min-width: 300px;
  }

  .notification-item {
    pointer-events: auto;
    transform-origin: top center;
  }
}

// Transition animations
.notification-enter-active {
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.notification-leave-active {
  transition: all 0.25s cubic-bezier(0.55, 0.055, 0.675, 0.19);
}

.notification-enter-from {
  opacity: 0;
  transform: translateY(-30px) scale(0.9);
}

.notification-leave-to {
  opacity: 0;
  transform: translateY(-20px) scale(0.95);
}

.notification-move {
  transition: transform 0.3s ease;
}

// Mobile responsive
@media (max-width: 600px) {
  .app-pop-up-notification-container {
    .notification-stack {
      align-items: stretch;
      padding: 4px;
    }

    .notification-item {
      transform-origin: top center;
    }
  }

  .notification-enter-from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
}
</style>
